import * as core from "@starsach/shared-types";
import { UserPreferencesSchema, type ResolvedFormat, type Role, type UserPreferences, type WhenOptions } from "@starsach/shared-types";

/**
 * Requirements section 13.1. Preferences come from /v1/me and are applied
 * here: the theme and motion as attributes on <html> that the CSS keys
 * off, the time zone, date format and time format through useFormat(). Nothing here is
 * sensitive, so the theme is also cached in localStorage for the first
 * paint before /v1/me answers.
 */

export const DEFAULT_PREFERENCES: UserPreferences = UserPreferencesSchema.parse({});

const THEME_CACHE_KEY = "starsach.theme";

export function detectedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function effectiveTimeZone(p: Pick<UserPreferences, "timeZone">): string {
  return p.timeZone === "auto" ? detectedTimeZone() : p.timeZone;
}

export function resolveTheme(theme: UserPreferences["theme"]): "light" | "dark" {
  if (theme !== "system") return theme;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveMotion(motion: UserPreferences["motion"]): "standard" | "reduced" {
  if (motion !== "auto") return motion;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "reduced" : "standard";
}

/** Writes the resolved theme and motion onto <html>. Returns a disposer that stops following the OS when "system"/"auto". */
export function applyAppearance(p: Pick<UserPreferences, "theme" | "motion">): () => void {
  const root = document.documentElement;
  const paint = () => {
    root.dataset["theme"] = resolveTheme(p.theme);
    root.dataset["motion"] = resolveMotion(p.motion);
  };
  paint();
  try {
    localStorage.setItem(THEME_CACHE_KEY, p.theme);
  } catch {
    // Private mode or a full store: the first paint just follows the OS.
  }
  const queries = [window.matchMedia?.("(prefers-color-scheme: dark)"), window.matchMedia?.("(prefers-reduced-motion: reduce)")].filter(Boolean) as MediaQueryList[];
  for (const q of queries) q.addEventListener("change", paint);
  return () => {
    for (const q of queries) q.removeEventListener("change", paint);
  };
}

/** The role-appropriate landing pages (13.1): what a user can choose to arrive at. */
export function landingPages(role: Role | undefined): Array<{ path: string; label: string }> {
  const common = [
    { path: "/", label: "Focus" },
    { path: "/journeys", label: "Journeys" },
    { path: "/flow-configuration", label: "Flow configuration" },
    { path: "/trusted-advisor", label: "Trusted Advisor" },
  ];
  return role === "internal-team"
    ? [...common, { path: "/customers", label: "Customers" }, { path: "/platform", label: "Platform" }]
    : [...common, { path: "/customers", label: "Environments" }];
}

export function isLandingPage(path: string, role: Role | undefined): boolean {
  return landingPages(role).some((l) => l.path === path);
}

/** What the cached theme says before /v1/me has answered; the inline script in index.html reads the same key. */
export function cachedTheme(): UserPreferences["theme"] {
  try {
    const t = localStorage.getItem(THEME_CACHE_KEY);
    return t === "light" || t === "dark" ? t : "system";
  } catch {
    return "system";
  }
}


/**
 * #449 - the formatters (crmarr/templates localization.md, ported from Stugum). The formatting itself lives in
 * `@starsach/shared-types` (format.ts, #650), so the email the dispatcher renders reads exactly like the portal;
 * what is here is what only a browser can answer - `auto` as the device's zone and the device's region.
 * `useFormat()` is the one way a page reaches them; the lint rule in eslint.config.mjs bans toLocale*String and
 * Intl.DateTimeFormat / NumberFormat everywhere else. `locale` is the device's (undefined) unless a test pins one:
 * the portal has one language, so there is no language preference, and the words it names (month abbreviations,
 * AM/PM) are English whatever the device. `timeFormat` is the clock, `auto` when absent - never read from `dateFormat`.
 */
export type FormatPrefs = Pick<UserPreferences, "timeZone" | "dateFormat"> & { timeFormat?: UserPreferences["timeFormat"]; locale?: string };
export type { WhenOptions };

type Value = string | number | Date;

function deviceLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  return (navigator.languages ?? [navigator.language]).filter(Boolean);
}

/** The tag whose hour cycle `timeFormat: auto` follows: English, in the device's region when the device speaks it. */
export function clockLocale(uiLocale: string | undefined, device: readonly string[] = deviceLanguages()): string {
  return core.clockLocale(uiLocale, device);
}

export const localeHourCycle = core.localeHourCycle;

/**
 * The clock in force (#449): the chosen one, or for `auto` the UI language's hour cycle in the device's region - a
 * pinned `locale` stands in for the device. Never derived from the date format.
 */
export function resolveTimeFormat(p: Pick<FormatPrefs, "timeFormat" | "locale">): "12h" | "24h" {
  if (p.timeFormat === "12h" || p.timeFormat === "24h") return p.timeFormat;
  return core.localeHourCycle(core.clockLocale(core.UI_LOCALE, p.locale ? [p.locale] : deviceLanguages()));
}

/** The preferences as the shared formatters take them: the zone and the clock resolved against this device. */
export function resolveFormat(p: FormatPrefs): ResolvedFormat {
  return { timeZone: effectiveTimeZone(p), dateFormat: p.dateFormat, clock: resolveTimeFormat(p), locale: p.locale };
}

export const formatWhen = (value: Value, p: FormatPrefs, opts: WhenOptions = {}) => core.formatWhen(value, resolveFormat(p), opts);
export const formatDate = (value: Value, p: FormatPrefs) => core.formatDate(value, resolveFormat(p));
export const formatTime = (value: Value, p: FormatPrefs, opts: WhenOptions = {}) => core.formatTime(value, resolveFormat(p), opts);
export const formatDay = (value: string, p: FormatPrefs) => core.formatDay(value, resolveFormat(p));
export const formatMonthDay = (value: Value, p: FormatPrefs) => core.formatMonthDay(value, resolveFormat(p));
export const formatMonth = (value: string, p: FormatPrefs) => core.formatMonth(value, resolveFormat(p));
export const formatNumber = (value: number, p: FormatPrefs, opts?: Intl.NumberFormatOptions) => core.formatNumber(value, p, opts);
export const formatMoney = (value: number, p: FormatPrefs, opts?: Intl.NumberFormatOptions) => core.formatMoney(value, p, opts);
export const plural = core.plural;
export const wallTimeToInstant = core.wallTimeToInstant;
